import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useCurrentClient } from 'src/clients/context/CurrentClientProvider';
import ConfirmDeleteModal from 'src/common/components/ConfirmDeleteModal';
import EmailContentModal, {
  type EditableEmailFields,
} from 'src/common/components/EmailContentModal';
import { useEmailDocument } from 'src/common/hooks/useEmailDocument';
import { GenericErrorPage } from 'src/error/GenericErrorPage';
import { useFlags } from 'src/feature-flags/FlagsHook';
import {
  useUpdateEmailContent,
  type EmailContentResponse,
} from 'src/meeting-note/hooks/useGenerateEmailContent';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import FileUploadLayout from './FileUploadLayout';
import { useGeneratedDocuments, type GeneratedDocument } from './hooks/useGeneratedDocuments';
import { useDeleteGeneratedDocument } from './hooks/useDeleteGeneratedDocument';
import { useDownloadDocumentFile } from './hooks/useDownloadDocumentFile';
import { useBulkDocumentSelection } from './hooks/useBulkDocumentSelection';
import EmptyView from './components/EmptyView';
import GenerateDocumentsControl from './components/GenerateDocumentsControl';
import GeneratedDocumentInlineActions from './components/GeneratedDocumentInlineActions';
import RenameDocumentControl from './components/RenameDocumentControl';
import SelectionHeaderActions from './components/SelectionHeaderActions';
import BulkDeleteConfirmModal from './components/BulkDeleteConfirmModal';
import {
  buildEditAction,
  buildGeneratedDocActions,
  emailTypeFor,
} from './components/generatedDocActions';
import {
  GeneratedDocumentType as DocumentType,
  GENERATED_DOCUMENT_TYPE_LABELS_PLURAL as DOCUMENT_TYPE_LABELS_PLURAL,
  matchesGeneratedDocumentSearch,
} from './components/generatedDocumentTypes';
import FilesTable from './files-table/FilesTable';
import {
  generatedDocumentCreatedColumn,
  generatedDocumentStatusColumn,
  generatedDocumentTypeColumn,
  toGeneratedDocumentRow,
  type GeneratedDocumentRow,
} from './files-table/generatedDocumentColumns';

type DocumentToDelete = {
  id: string;
  displayName: string;
  documentType: DocumentType;
};

type SiestaAIGeneratedProps = {
  documentTypeFilter?: DocumentType;
};

export default function SiestaAIGenerated({ documentTypeFilter }: SiestaAIGeneratedProps) {
  const { documents, isLoading, error, refetch } = useGeneratedDocuments();
  const { clientId, client } = useCurrentClient();
  const flags = useFlags();
  const {
    deleteDocument,
    deleteErrorFor,
    resetErrors,
    isDeleting: isDeletingDocument,
  } = useDeleteGeneratedDocument();
  const { downloadFile, downloadFileViaAnchor } = useDownloadDocumentFile({ clientId });

  const { handleEmailClick, handleDeleteEmail, isDeleting: isDeletingEmail } = useEmailDocument();
  const { updateEmailContent, isSaving } = useUpdateEmailContent();

  const navigate = useNavigate();

  const openFlow = (documentType: DocumentType) =>
    navigate(`/client/${clientId}/${documentType}`, { state: { fromPath: location.pathname } });

  const renderGenerateControl = (buttonText: string) => (
    <GenerateDocumentsControl
      documentTypeFilter={documentTypeFilter}
      openFlow={openFlow}
      buttonText={buttonText}
    />
  );

  const [searchValue, setSearchValue] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<DocumentToDelete | null>(null);
  const [emailContent, setEmailContent] = useState<EmailContentResponse | null>(null);
  const [emailDocumentLabel, setEmailDocumentLabel] = useState<string | undefined>();
  const [emailModalOpen, setEmailModalOpen] = useState(false);

  const handleDeletePress = (document: DocumentToDelete) => {
    resetErrors();
    setDocumentToDelete(document);
  };

  const deleteError = documentToDelete ? deleteErrorFor(documentToDelete.documentType) : null;

  const handleEmailPress = async (document: GeneratedDocument) => {
    const emailType = emailTypeFor(document);
    if (!emailType) return;

    const result = await handleEmailClick(document.id, emailType);
    if (result) {
      setEmailContent(result);
      setEmailDocumentLabel(document.displayName);
      setEmailModalOpen(true);
    }
  };

  const handleDeleteEmailContent = () => {
    if (!emailContent) return;
    handleDeleteEmail(emailContent.id);
    setEmailContent(null);
    setEmailModalOpen(false);
  };

  const handleSaveEmailContent = async (fields: EditableEmailFields) => {
    if (!emailContent) return;
    try {
      await updateEmailContent({ emailContentId: emailContent.id, ...fields });
      setEmailContent(prev => (prev ? { ...prev, ...fields } : prev));
    } catch (e) {
      notifyAndLogError(
        'Failed to save email draft',
        'An error occurred while saving the email draft. Please try again.',
        e,
        { emailContentId: emailContent.id },
      );
    }
  };

  const handleDeleteConfirm = async () => {
    if (!documentToDelete) return;
    try {
      await deleteDocument(clientId, documentToDelete.id, documentToDelete.documentType);
    } catch (e) {
      notifyAndLogError(
        'Failed to delete document',
        'An error occurred while trying to delete the document. Please try again later.',
        e,
        { clientId, documentId: documentToDelete.id, documentType: documentToDelete.documentType },
      );
    }
    setDocumentToDelete(null);
    await refetch();
  };

  const title = documentTypeFilter
    ? DOCUMENT_TYPE_LABELS_PLURAL[documentTypeFilter]
    : 'Generated documents';

  const typedDocuments = documentTypeFilter
    ? documents.filter(doc => doc.documentType === documentTypeFilter)
    : documents;

  const filteredDocuments = searchValue.trim()
    ? typedDocuments.filter(doc => matchesGeneratedDocumentSearch(doc, searchValue))
    : typedDocuments;

  const additionalColumns = documentTypeFilter
    ? [generatedDocumentCreatedColumn, generatedDocumentStatusColumn]
    : [generatedDocumentCreatedColumn, generatedDocumentTypeColumn];

  const rows = filteredDocuments.map(toGeneratedDocumentRow);

  const {
    selection,
    selectedRecords: selectedRows,
    bulkDeleteNames,
    requestBulkDelete,
    cancelBulkDelete,
    confirmBulkDelete,
    isBulkDeleting,
    isBulkDownloading,
    downloadSelected,
  } = useBulkDocumentSelection({
    records: rows,
    entityName: 'document',
    nameOf: row => row.document.displayName,
    deleteRecord: row => deleteDocument(clientId, row.document.id, row.document.documentType),
    downloadRecord: row => {
      if (row.document.fileId == null) return;
      return downloadFileViaAnchor(row.document.fileId, row.document.displayName);
    },
    refetch,
    logContext: { clientId },
  });
  const hasDownloadableSelection = selectedRows.some(row => row.document.fileId != null);

  const renderNameSuffix = (row: GeneratedDocumentRow) =>
    row.document.renameFileId !== undefined ? (
      <RenameDocumentControl
        fileId={row.document.renameFileId}
        currentName={row.document.displayName}
        variant="plain"
      />
    ) : null;

  const renderInlineActions = (row: GeneratedDocumentRow) => (
    <GeneratedDocumentInlineActions
      doc={row.document}
      onDownload={downloadFile}
      editAction={buildEditAction(row.document, clientId, navigate, flags)}
      showGeneratingIndicator={!documentTypeFilter}
    />
  );

  if (error) {
    return <GenericErrorPage error={error} />;
  }

  if (typedDocuments.length === 0 && !isLoading) {
    return (
      <FileUploadLayout title={title} variant="compact">
        <EmptyView
          title="Your AI-generated documents will appear here"
          subtitle="Generate suitability reports, meeting agendas, and data exports with Siesta AI. All your AI-created documents will be organised in one convenient location."
          customCta={renderGenerateControl('Generate new')}
        />
      </FileUploadLayout>
    );
  }

  return (
    <FileUploadLayout
      title={selection.isSelecting ? 'Select the files' : title}
      variant="compact"
      collapsibleSearch={!selection.isSelecting}
      searchOpen={searchOpen}
      onSearchOpenChange={setSearchOpen}
      searchValue={searchValue}
      onSearchChange={setSearchValue}
      searchResultCount={filteredDocuments.length}
      searchPlaceholder="Search documents"
      actions={
        selection.isSelecting ? (
          <SelectionHeaderActions
            selectedCount={selectedRows.length}
            onDeleteSelected={requestBulkDelete}
            onDownloadSelected={downloadSelected}
            onCancel={selection.exit}
            isDownloading={isBulkDownloading}
            downloadDisabled={!hasDownloadableSelection}
          />
        ) : (
          renderGenerateControl('Generate')
        )
      }
    >
      <FilesTable<GeneratedDocumentRow>
        entitiesWithFile={rows}
        isLoading={isLoading}
        additionalColumns={additionalColumns}
        nameSubtitle={() => undefined}
        nameSuffix={renderNameSuffix}
        inlineActions={renderInlineActions}
        actions={row =>
          buildGeneratedDocActions({
            doc: row.document,
            clientId,
            navigate,
            flags,
            includeEdit: false,
            onEmail: () => handleEmailPress(row.document),
            onSelect: () => selection.enter(row.id),
            onDelete: () =>
              handleDeletePress({
                id: row.document.id,
                displayName: row.document.displayName,
                documentType: row.document.documentType,
              }),
          })
        }
        variant="compact"
        nameColumnTitle="File name"
        selection={
          selection.isSelecting
            ? {
                selectedIds: selection.selectedIds,
                onToggle: selection.toggle,
              }
            : undefined
        }
      />

      <ConfirmDeleteModal
        opened={!!documentToDelete}
        onClose={() => setDocumentToDelete(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete document"
        message={`Are you sure you want to delete "${documentToDelete?.displayName}"? This action cannot be undone.`}
        error={deleteError}
        isLoading={isDeletingDocument}
      />

      <BulkDeleteConfirmModal
        fileNames={bulkDeleteNames}
        entityName="document"
        onClose={cancelBulkDelete}
        onConfirm={confirmBulkDelete}
        isLoading={isBulkDeleting}
      />

      <EmailContentModal
        opened={emailModalOpen}
        onClose={() => setEmailModalOpen(false)}
        content={emailContent}
        onDelete={handleDeleteEmailContent}
        onSave={handleSaveEmailContent}
        isDeleting={isDeletingEmail}
        isSaving={isSaving}
        clientName={client ? `${client.firstName} ${client.lastName}` : undefined}
        documentLabel={emailDocumentLabel}
      />
    </FileUploadLayout>
  );
}
